import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CardContent, Backdrop, CircularProgress, Typography, Snackbar, Alert, Stack } from '@mui/material';
import { MedicalServices as MedNecIcon, Add as AddIcon, UploadFile as UploadIcon } from '@mui/icons-material';
import { PageContainer, PageCard, DashboardHeader, StatusPage, PageLoading, CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import { useMedNecContext } from '../hooks/useMedNecContext';
import { useMedNecDashboardData } from './useMedNecDashboardData';
import type { MedNecStatus } from '../utils/statusConfig';
import WorkflowDashboardGrid from './WorkflowDashboardGrid';
import { useIngestSchedulingPacketMutation } from '../../../services/MedNecService';

export const MedNecDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { company, center, isConfigured } = useMedNecContext();
  const { handleCenterChange } = useCenterSelection();

  const { statusCounts, totalCount, isLoading, hasError } = useMedNecDashboardData(center);
  const [ingestPacket, { isLoading: isIngesting }] = useIngestSchedulingPacketMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  if (!company) {
    return (
      <PageContainer>
        <StatusPage
          variant="configuration-required"
          icon={MedNecIcon}
          title="Configuration Required"
          message="To access the medical necessity dashboard, please ensure company is properly configured in your session."
          chips={[
            { label: `Company: ${company || 'Not configured'}`, color: company ? 'success' : 'warning' },
          ]}
          footnote="Contact your system administrator if you need assistance with configuration"
        />
      </PageContainer>
    );
  }

  const handleStatusClick = (status: MedNecStatus) => {
    const params = new URLSearchParams();
    params.append('status', status);
    if (company) params.append('company', company);
    if (center) params.append('center', center);
    navigate(`/mednec/worklist?${params.toString()}`);
  };

  const handleNewCase = () => {
    const params = new URLSearchParams();
    if (company) params.append('company', company);
    if (center) params.append('center', center);
    navigate(`/mednec/cases/new?${params.toString()}`);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be re-selected
    e.target.value = '';

    if (file.type !== 'application/pdf') {
      setErrorMessage('Only PDF files are accepted.');
      return;
    }

    try {
      const result = await ingestPacket({ facility: center, file }).unwrap();

      if (result.success && result.caseId) {
        const params = new URLSearchParams();
        params.append('id', result.caseId);
        if (company) params.append('company', company);
        if (center) params.append('center', center);
        navigate(`/mednec/cases/edit?${params.toString()}`);
      } else {
        setErrorMessage(result.errorMessage || 'Ingestion failed. Please try again.');
      }
    } catch {
      setErrorMessage('An unexpected error occurred during ingestion. Please try again.');
    }
  };

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          minWidth: '1000px',
          maxWidth: '1400px',
          width: '100%',
        }}>
          <DashboardHeader
            icon={MedNecIcon}
            title="Medical Necessity"
            stats={[{ label: 'Active Cases', value: totalCount }]}
            actions={[
              { label: 'Upload Packet', icon: UploadIcon, onClick: handleUploadClick, variant: 'outlined', disabled: isIngesting || !center },
              { label: 'New Case', icon: AddIcon, onClick: handleNewCase, disabled: !center },
            ]}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={handleFileSelected}
          />

          <CardContent sx={{ p: 4, backgroundColor: 'transparent' }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelector value={center} onChange={handleCenterChange} />
            </Box>
            {!center ? (
              <StatusPage
                variant="configuration-required"
                icon={MedNecIcon}
                title="Select a Center"
                message="Please select a medical center from the dropdown above to view the dashboard."
              />
            ) : isLoading ? (
              <PageLoading message="Loading Dashboard" subMessage="Retrieving medical necessity data..." />
            ) : hasError ? (
              <StatusPage
                variant="error"
                title="Unable to Load Medical Necessity Dashboard"
                message="There was an issue retrieving medical necessity data from the system. Please check your connection and try again, or contact IT support if the problem continues."
              />
            ) : (
              <WorkflowDashboardGrid
                statusCounts={statusCounts}
                onStatusClick={handleStatusClick}
              />
            )}
          </CardContent>
        </PageCard>
      </Box>

      {/* Processing overlay */}
      <Backdrop open={isIngesting} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6">Processing Scheduling Packet...</Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>Extracting case data with AI vision. This may take a moment.</Typography>
      </Backdrop>

      {/* Error snackbar */}
      <Snackbar open={!!errorMessage} autoHideDuration={8000} onClose={() => setErrorMessage(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setErrorMessage(null)} variant="filled">{errorMessage}</Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default MedNecDashboard;
